import { type ClipboardPermissionResult } from '@canvas/clipboard/permission';
import { type CheatCodeCapture } from '@canvas/input-types/cheat-event-types';
import {
  type KeyboardDownCapture,
  type KeyboardShortcutCapture,
} from '@canvas/input-types/keyboard-events-types';
import {
  type PointerPosition,
  type PointerDownCapture,
  type PointerButton,
  type PointerDelta,
} from '@canvas/input-types/pointer-events-types';
import { Keys } from '@canvas/keys';
import { getOperatingSystem } from '@canvas/shortcuts';
import { ShortcutKeyMap } from '@canvas/shortcuts/keymap';
import { type ShortcutModifier, type DOMModifier } from '@canvas/shortcuts/types';
import { sortModifiers } from '@canvas/shortcuts/utils';

import { setupClipboardEvents } from './device/clipboard-events.ts';
import { setupDragEvents } from './device/drag-events.ts';
import { setupFocusEvents } from './device/focus-events.ts';
import { setupIgnoredEvents } from './device/ignored-events.ts';
import { setupKeyboardEvents } from './device/keyboard-events.ts';
import { type GetUserPreference, setupPointerEvents } from './device/pointer-events.ts';
import { setupClipboardPermissions } from './device/setup-clipboard-permissions.ts';
import { setupSyntheticEvents } from './device/synthetic-events.ts';
import {
  type CanvasEventTarget,
  type Capture,
  type CaptureContext,
  type CaptureListener,
  type CaptureType,
} from './input-types.ts';

export type { CaptureContext, PointerDownCapture };

const listenerTargets = [
  'pasteError',
  'wheel',
  'keyboardDown',
  'keyboardUp',
  'pointerDown',
];

export const initialCaptureContext: CaptureContext = {
  x: undefined,
  y: undefined,
  key: undefined,
  button: 'none' as PointerButton,
  touchLocations: [],
  lastPointerDown: undefined,
  lastPointerDownExpired: false,
  currentCaptureId: 0,
  clipboardReadPermission: 'denied' as ClipboardPermissionResult,
  clipboardWritePermission: 'denied' as ClipboardPermissionResult,
};

export interface InputCaptureOptions {
  disableCanvasScroll?: boolean;
  disableIgnoredEvents?: boolean;
  /**
   * Registers keyboard listeners in DOM capture phase instead of bubble phase.
   * Use this when descendants may stop propagation but the shared input stream
   * still needs to observe the original keyboard event.
   */
  useCapturePhaseKeyboardEvents?: boolean;
}

const konamiCode = [
  Keys.ArrowUp,
  Keys.ArrowUp,
  Keys.ArrowDown,
  Keys.ArrowDown,
  Keys.ArrowLeft,
  Keys.ArrowRight,
  Keys.ArrowLeft,
  Keys.ArrowRight,
  Keys.b,
  Keys.a,
  Keys.Enter,
].toString();

export class InputCapture {
  public stream: Capture[] = [];

  public captureContext: CaptureContext = { ...initialCaptureContext };

  private cleanupHandlers: VoidFunction[] = [];

  private disposed = false;
  private attachWheel: VoidFunction = () => {};
  private detachWheel: VoidFunction = () => {};

  private captureOutsideWindow = false;
  private rotationEnabled = false;
  private os = getOperatingSystem();
  private keymaps: Record<string, ShortcutKeyMap<string>> = {};
  private keySequence: string[] = [];
  private cheatCodeSequence: string[] = [];
  private keyModifiers: Set<ShortcutModifier> = new Set();
  private listeners: Map<CaptureType, CaptureListener> = new Map();
  /**
   * @param root HTMLElement: Which element of the DOM we will listen to (e.g. document.body)
   * @param getUserPreference Optional callback to read user preferences
   * @param options Optional configuration for input capture behavior
   * @returns InputCapture: A new instance of InstanceCapture
   */
  public static from(
    root: HTMLElement,
    getUserPreference?: GetUserPreference,
    options?: InputCaptureOptions,
  ) {
    return new InputCapture(root, getUserPreference, options);
  }

  private constructor(
    root: HTMLElement,
    getUserPreference?: GetUserPreference,
    options?: InputCaptureOptions,
  ) {
    const pointerEvents = setupPointerEvents(
      root,
      this.id,
      this.os,
      this.onCapture,
      this.captureContext,
      this.onKeyboardModifierDown,
      this.onKeyboardModifierUp,
      () => this.captureOutsideWindow,
      () => this.rotationEnabled,
      getUserPreference,
      options?.disableCanvasScroll ?? false,
    );
    this.attachWheel = pointerEvents.attachWheel;
    this.detachWheel = pointerEvents.detachWheel;

    this.cleanupHandlers.push(
      pointerEvents.cleanup,
      setupDragEvents(root, this.id, this.onCapture, this.captureContext, this.listeners),
      setupFocusEvents(root, this.id, this.onCapture),
      setupKeyboardEvents(
        root,
        this.id,
        this.os,
        this.processKeyboardShortcuts,
        this.processCheatCodes,
        this.onCapture,
        this.onKeyboardModifierDown,
        this.onKeyboardModifierUp,
        options?.useCapturePhaseKeyboardEvents ?? false,
      ),
      options?.disableIgnoredEvents ? () => {} : setupIgnoredEvents(root),
      setupClipboardEvents(
        root,
        this.id,
        this.onCapture,
        this.captureContext,
        this.listeners,
      ),
      setupSyntheticEvents(
        root,
        this.id,
        this.onCapture,
        this.captureContext,
        this.listeners,
      ),
    );
    setupClipboardPermissions(this.captureContext);
  }

  public on(captureType: CaptureType, listener: CaptureListener) {
    this.listeners.set(captureType, listener);
  }

  /**
   * Attaches or detaches the canvas wheel listener at runtime.
   *
   * The load-time `disableCanvasScroll` option still determines the initial
   * state; this lets callers re-enable scrolling on command (e.g. when a native
   * embed is maximized) and disable it again on return to the inline view. It is
   * idempotent and a no-op once the capture has been disposed.
   */
  public setCanvasScrollEnabled(enabled: boolean) {
    if (this.disposed) {
      return;
    }
    if (enabled) {
      this.attachWheel();
    } else {
      this.detachWheel();
    }
  }

  private onCapture = (capture: Capture) => {
    if (this.lastCapture) {
      if (
        (capture.type === 'pointerMove' && this.lastCapture.type === 'pointerMove') ||
        (capture.type === 'touchMove' && this.lastCapture.type === 'touchMove')
      ) {
        this.lastCapture.properties.x = capture.properties.x;
        this.lastCapture.properties.y = capture.properties.y;
        this.lastCapture.properties.deltaX += capture.properties.deltaX;
        this.lastCapture.properties.deltaY += capture.properties.deltaY;

        if (capture.type === 'touchMove' && this.lastCapture.type === 'touchMove') {
          this.lastCapture.properties.deltaDistance *= capture.properties.deltaDistance;
        }

        return;
      }
    }

    this.stream.push(capture);

    if (capture.type === 'pointerLeave') {
      this.keyModifiers.clear();
      this.keySequence.length = 0;
    }

    if (listenerTargets.includes(capture.type)) {
      this.listeners.get(capture.type)?.(capture);
    }
  };

  private processKeyboardShortcuts = (
    capture: KeyboardDownCapture,
    preventDefault?: VoidFunction,
  ) => {
    // Doing an explicit check as we've observed in practice that it's possible
    // for `key` to be undefined - https://atlassian-2y.sentry.io/issues/4056138476/
    if (!capture.properties.key) {
      return null;
    }

    if (!this.keymaps[capture.target]) {
      this.keySequence.push(capture.properties.key.toLowerCase());
      return null;
    }

    // Sort the key modifiers so that we can compare them in a consistent order, i.e.
    // Shift-Control vs Control-Shift
    const keyModifiersSorted = sortModifiers(Array.from(this.keyModifiers));
    this.keySequence = [...keyModifiersSorted, capture.properties.key.toLowerCase()];

    const shortcut = this.keymaps[capture.target].get(this.keySequence);
    if (!shortcut) {
      return null;
    }

    const event: KeyboardShortcutCapture = {
      id: this.id(),
      type: 'keyboardShortcut',
      target: capture.target,
      properties: { id: shortcut.id, code: capture.properties.code, cancelled: false },
    };
    this.stream.push(event);

    this.captureContext.key = capture.properties.key;

    this.keySequence.length = 0;

    if (shortcut.preventDefault) {
      preventDefault?.();
    }

    this.listeners.get('keyboardShortcut')?.(event);

    // Return a function to cancel the event
    return () => {
      event.properties.cancelled = true;
    };
  };

  private processCheatCodes = (capture: KeyboardDownCapture) => {
    if (!capture.properties.key) {
      return;
    }
    // Permit users to use upper or lower case A and B
    const key =
      capture.properties.key === Keys.A
        ? Keys.a
        : capture.properties.key === Keys.B
          ? Keys.b
          : capture.properties.key;
    // We want to ignore the shift modifier if someone wants to input a capital letter
    if (key !== Keys.Shift) {
      this.cheatCodeSequence.push(key);
    }

    const cheatCodeSequence = this.cheatCodeSequence.toString();
    if (konamiCode === cheatCodeSequence) {
      const event: CheatCodeCapture = {
        id: this.id(),
        type: 'cheatCode',
        target: capture.target,
        properties: { code: 'konami-code' },
      };
      this.stream.push(event);
      this.cheatCodeSequence.length = 0;
    } else if (!konamiCode.startsWith(cheatCodeSequence)) {
      this.cheatCodeSequence.length = 0;
    }
  };

  private id = (): number => {
    return this.captureContext.currentCaptureId++;
  };

  private onKeyboardModifierDown = (modifier: ShortcutModifier) => {
    this.keyModifiers.add(modifier);
  };

  private onKeyboardModifierUp = (modifier: ShortcutModifier) => {
    this.keyModifiers.delete(modifier);
  };

  public addKeyMap<T extends string>(
    target: CanvasEventTarget,
    keymap: ShortcutKeyMap<T>,
  ) {
    this.keymaps[target] = keymap;
  }

  /**
   * Returns whether any stream has a length, in which case we need to process
   */
  get needsProcessing(): boolean {
    return this.stream.length > 0;
  }

  get lastCapture(): Capture | undefined {
    return this.stream[this.stream.length - 1];
  }

  /**
   * Enables capturing and processing of certain events (e.g. pointer events) when the
   * pointer is outside the window.
   *
   * This is disabled by default.
   *
   * @remarks This is useful for scenarios where you want to capture events even when the
   * pointer is outside the window, e.g. when dragging something past the window
   * boundary. To avoid unexpected behavior, you should disable this with
   * {@link disableCaptureOutsideWindow} when you no longer need it.
   */
  public enableCaptureOutsideWindow() {
    this.captureOutsideWindow = true;
  }

  /**
   * Disables capturing and processing of certain events (e.g. pointer events) when the
   * pointer is outside the window.
   *
   * This is the default behavior.
   */
  public disableCaptureOutsideWindow() {
    this.captureOutsideWindow = false;
  }

  public setRotationEnabled(enabled: boolean) {
    this.rotationEnabled = enabled;
  }

  public pointerPosition = (): PointerPosition => {
    return {
      x: this.captureContext.x ?? 0,
      y: this.captureContext.y ?? 0,
    };
  };

  public pointerLastDownAt = (
    ignoreExpiration = false,
  ): PointerDownCapture | undefined => {
    if (ignoreExpiration) {
      return this.captureContext.lastPointerDown;
    }

    return this.captureContext.lastPointerDownExpired
      ? undefined
      : this.captureContext.lastPointerDown;
  };

  public pointerMoved = (): PointerDelta => {
    const delta = { x: 0, y: 0 };

    this.stream.forEach((capture) => {
      if (capture.type === 'pointerMove') {
        delta.x += capture.properties.deltaX;
        delta.y += capture.properties.deltaY;
      }
    });

    return delta;
  };

  public touchMoved = (): PointerDelta => {
    const delta = { x: 0, y: 0 };

    this.stream.forEach((capture) => {
      if (capture.type === 'touchMove') {
        delta.x += capture.properties.deltaX;
        delta.y += capture.properties.deltaY;
      }
    });

    return delta;
  };

  public touchZoomed = (): number => {
    let deltaDistance = 1;

    this.stream.forEach((capture) => {
      if (capture.type === 'touchMove') {
        deltaDistance *= capture.properties.deltaDistance;
      }
    });

    return deltaDistance - 1;
  };

  public wheeledBy = (): PointerDelta => {
    const delta = { x: 0, y: 0 };

    for (let i = this.stream.length - 1; i >= 0; i--) {
      const capture = this.stream[i];

      // Simply take the last wheel event and use its delta. We used to accumulate this
      // value, but this ends up causing far too much jumping around at low frame rates.
      if (capture.type === 'wheel') {
        delta.x = -capture.properties.deltaX;
        delta.y = -capture.properties.deltaY;
        break;
      }
    }

    return delta;
  };

  public hasKeyModifierPressed(domKey: DOMModifier) {
    const shortcutModifier = ShortcutKeyMap.fromDOMModifier(this.os, domKey);
    return this.keyModifiers.has(shortcutModifier);
  }

  public keySequenceIncludesKey(key: string) {
    return this.keySequence.some(
      (k) => k.toLocaleLowerCase() === key.toLocaleLowerCase(),
    );
  }

  /**
   * Detects if an interaction includes pressing of any new tab modifier
   * buttons. Centralised into @canvas/input due to being input-specific logic.
   * @returns true if interaction suggests a new-tab interaction.
   */
  public hasNewTabModifierPressed() {
    return (
      this.keyModifiers.size === 1 &&
      (this.keyModifiers.has('Control') ||
        this.keyModifiers.has('Shift') ||
        this.keyModifiers.has('Meta') ||
        this.keyModifiers.has('Command'))
    );
  }

  /**
   * Gets all of our pointer events in a particular order as expected of our system
   * @returns Array of captured events with properties.
   */
  getEvents = (): Capture[] => {
    return this.stream;
  };

  /**
   * Clears this InputCapture's stream of events
   */
  clear = (): void => {
    this.stream.length = 0;
  };

  /**
   * Convenient for debugging purposes
   */
  summary = (): void => {
    console.log(JSON.stringify(this.stream, null, 2));
  };

  /**
   * Retrieves events currently in the stream for debugging purposes.
   */
  report = (events = this.stream) => {
    const report: Record<string, number> = {};

    for (const event of events) {
      const entry = `${event.type}:${event.target}`;

      if (!report[entry]) {
        report[entry] = 0;
      }
      report[entry]++;
    }

    return Object.keys(report).map((k) => `${k}:${report[k]}`);
  };

  /**
   * Cleanup input capture and all events it has registered.
   */
  dispose() {
    this.disposed = true;
    this.cleanupHandlers.forEach((handler) => handler());

    this.stream.length = 0;

    this.keymaps = {};
    this.keySequence.length = 0;
    this.cheatCodeSequence.length = 0;
    this.keyModifiers.clear();

    this.cleanupHandlers.length = 0;

    this.listeners.clear();
  }

  /**
   * Gets the current listeners for a given capture type.
   */
  getListeners = (captureType: CaptureType) => {
    return this.listeners.get(captureType);
  };
}
